import { useInfiniteQuery, useMutation, useQuery } from 'react-query';

import { useServiceError } from '@/hooks';

import {
  CreatePaymentConditionRequest,
  ListPaymentConditionsQuery,
  PaymentCondition,
  UpdatePaymentConditionRequest,
} from './PaymentCondition.types';

import {
  activateOrInactivatePaymentCondition,
  createPaymentCondition,
  deletePaymentCondition,
  listPaymentsCondition,
  listPaymentsConditionReduced,
  paymentConditionId,
  updatePaymentCondition,
} from './PaymentCondition.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function usePaymentsCondition(params?: ListPaymentConditionsQuery) {
  const query = useInfiniteQuery(
    ['paymentCondition', { ...params }],
    ({ pageParam }) => {
      const req: ListPaymentConditionsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listPaymentsCondition(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function usePaymentsConditionReduced(
  params?: ListPaymentConditionsQuery,
  enabled = true,
) {
  const cache = queryClient.getQueryData([
    'paymentCondition/reduced',
    { ...params },
  ]);

  return useInfiniteQuery(
    ['paymentCondition/reduced', { ...params }],
    ({ pageParam }) => {
      const req: ListPaymentConditionsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listPaymentsConditionReduced(req);
    },
    {
      enabled: enabled && !cache,
      onError(error) {
        notificationError(error);
      },
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );
}

export function usCreatePaymentCondition() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: CreatePaymentConditionRequest) => createPaymentCondition(obj),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'paymentConditions.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'paymentConditions.create.notification.successDescription',
          }),
        });

        queryChanel({
          invalidateQueries: [['paymentCondition']],
          resetQueries: [['paymentCondition/reduced']],
        });
      },
      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('duplicate key value violates unique constraint')
                ? 'Já existe uma condição de pagamento com este nome'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useUpdatePaymentCondition() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: UpdatePaymentConditionRequest) => updatePaymentCondition(obj),
    {
      async onSuccess(_, vars) {
        notification.success({
          message: intl.formatMessage({
            id: 'paymentConditions.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'paymentConditions.update.notification.successDescription',
          }),
        });

        queryChanel({
          invalidateQueries: [['paymentCondition']],
          resetQueries: [
            ['paymentCondition/reduced'],
            ['paymentCondition', vars.id],
          ],
        });
      },
    },
  );

  return mutation;
}

export function useDeletePaymentCondition() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  return useMutation((id: number) => deletePaymentCondition(id), {
    async onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'paymentConditions.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'paymentConditions.remove.notification.successDescription',
        }),
      });

      queryChanel({
        invalidateQueries: [['paymentCondition']],
        resetQueries: [['paymentCondition/reduced']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function usePaymentConditionMutate() {
  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<PaymentCondition>([
        'paymentCondition',
        id,
      ]);

      if (!!cache) return cache;

      return paymentConditionId(id);
    },
    {
      onSuccess(data, id) {
        queryClient.setQueryData(['paymentCondition', id], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function usePaymentCondition(id?: number, enabled = true) {
  const cache = queryClient.getQueryData<PaymentCondition>([
    'paymentCondition',
    id,
  ]);

  return useQuery(['paymentCondition', id], () => paymentConditionId(id), {
    enabled: !!id && enabled && !cache,
    onError(error) {
      notificationError(error);
    },
  });
}

export function useActiveOrInactivatePaymentCondition() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (paymentConditionId: number) =>
      activateOrInactivatePaymentCondition(paymentConditionId),
    {
      onSuccess(_, paymentConditionId) {
        message.success(
          intl.formatMessage({
            id: 'paymentConditions.status.message.success',
          }),
        );

        queryChanel({
          invalidateQueries: [['paymentCondition']],
          resetQueries: [
            ['paymentCondition/reduced'],
            ['paymentCondition', paymentConditionId],
          ],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
